import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { TriggerProps } from '@atlaskit/popup/types';
import { publish, useSubscribe } from '@atlaskit/rovo-triggers/main';
import { Topics, type Payload } from '@atlaskit/rovo-triggers/types';

import { RemixContainer } from '@atlassian/malleable-ui-components/remix';
import {
	PAGE_REMIX_OPTIONS,
	getTypeDisplayName,
} from '@atlassian/malleable-ui-components/remix/remix-overlay/utils';
import remixOverlayMessages from '@atlassian/malleable-ui-components/remix/remix-overlay/messages';
import type {
	RemixOptionSelectionParameters,
	RemixOptionKind,
} from '@atlassian/malleable-ui-components/remix/remix-overlay/types';
import type { InteractionSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import {
	ExperienceName,
	REMIX_GENERATE_PREVIEW_MARKS,
	remixGeneratePreviewExperience,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	abortRemixCreateExperience,
	abortRemixCreateExperienceIfNotCommitted,
	startRemixCreateExperience,
	succeedRemixCreateExperienceAtHandoff,
	trackRemixCreateOptionSelected,
} from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { getRemixFeatureArea } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-feature-area';
import type { RemixContentComponentProps } from '@atlassian/malleable-ui-components/types';

import { ConfluenceConversationAssistant } from '@confluence/conversation-assistant/entry-points/main.ts';
import { expValEquals, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { useContentType, usePageContentId } from '@confluence/page-context';
import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

import { resolveRemixDynamicUiContentType, type DynamicUiSource } from './common-types.ts';
import { flattenDocContentForPageRemixFragment } from './flattenDocContentForPageRemixFragment.ts';
import { useRemixThinkingBarProgress } from './useRemixThinkingBarProgress.ts';
import { useShouldHideDiagramRemixOption } from './useShouldHideDiagramRemixOption.ts';
import { useRemixToAIFC } from './useRemixToAIFC.ts';

const PAGE_REMIX_ENTRY_POINT = 'objectSidebar';
const PAGE_REMIX_SOURCE = 'remixModalFromObjectLevelSidebar';
export const PAGE_REMIX_CONSUME_ONCE_KEY = 'renderer-page-remix';
const EMPTY_PAGE_ADF: DocNode = { type: 'doc', version: 1, content: [] };

const publishPageRemixHandoffFailed = () => {
	publish(Topics.ROVO_REMIX, {
		type: 'rovo-remix-handoff-failed',
		source: PAGE_REMIX_CONSUME_ONCE_KEY,
	} as unknown as Payload);
};

const isRendererPageSubmit = (payload: Payload): boolean =>
	payload.type === 'submit-rovo-remix' &&
	payload.data.remixConsumer === 'renderer' &&
	payload.data.remixPreset === 'page';

const isRequestRovoPageRemix = (payload: Payload): boolean =>
	(payload as { type: string }).type === 'request-rovo-page-remix';

const isEmptyPageAdf = (doc: DocNode | null | undefined): boolean => {
	if (!doc || doc.type !== 'doc') {
		return true;
	}
	const content = doc.content;
	if (!content || content.length === 0) {
		return true;
	}
	return content.every(
		(node) =>
			node.type === 'paragraph' &&
			(!('content' in node) || !node.content || node.content.length === 0),
	);
};

type PageRemixRequestParameters = RemixOptionSelectionParameters & { conversationId?: string };

// TODO: Consolidate with ListenForSelectedContentRemixSubmit. Both listen for a Rovo Remix
// submit, start the existing native Remix path once, and stand down if the user closes Rovo,
// starts a new chat, opens a different Remix entry, or navigates. They share that skeleton and
// should be one component, even though one handles the whole page and the other handles
// selected text.
/** Listens for whole-page Rovo Remix submit and starts the existing page remix path. */
const PageRemixSubmitListener = ({
	currentContentId,
	hasGenerationStarted,
	onAcceptSubmit,
	onDisarm,
	originContentIdRef,
}: {
	currentContentId: string | undefined;
	hasGenerationStarted: boolean;
	onAcceptSubmit: (params: PageRemixRequestParameters) => Promise<void>;
	onDisarm: () => void;
	originContentIdRef: React.MutableRefObject<string | undefined>;
}) => {
	const hasGenerationStartedRef = useRef(hasGenerationStarted);
	hasGenerationStartedRef.current = hasGenerationStarted;
	const acceptedSubmitRef = useRef(false);
	const isArmedRef = useRef(false);
	const onAcceptSubmitRef = useRef(onAcceptSubmit);
	onAcceptSubmitRef.current = onAcceptSubmit;
	const onDisarmRef = useRef(onDisarm);
	onDisarmRef.current = onDisarm;

	const disarmIfIdle = useCallback(() => {
		if (hasGenerationStartedRef.current || acceptedSubmitRef.current) {
			return;
		}
		isArmedRef.current = false;
		originContentIdRef.current = undefined;
		onDisarmRef.current();
	}, [originContentIdRef]);

	const acceptPageRequest = useCallback(
		(params: PageRemixRequestParameters) => {
			const originContentId = originContentIdRef.current;
			if (originContentId && currentContentId && originContentId !== currentContentId) {
				return;
			}

			if (acceptedSubmitRef.current || hasGenerationStartedRef.current) {
				return;
			}

			acceptedSubmitRef.current = true;
			void onAcceptSubmitRef.current(params).catch(() => {
				acceptedSubmitRef.current = false;
			});
		},
		[currentContentId, originContentIdRef],
	);

	useSubscribe(
		{ topic: Topics.ROVO_REMIX, consumeOnceKey: PAGE_REMIX_CONSUME_ONCE_KEY },
		(payload) => {
			if (isRequestRovoPageRemix(payload)) {
				const data = (payload as { data?: Record<string, unknown> }).data ?? {};
				const type = (data.remixOption ?? data.type) as RemixOptionKind | undefined;
				if (!type) {
					return;
				}
				acceptPageRequest({
					type,
					subType: (data.remixSubType ?? data.subType) as string | undefined,
					userPrompt: data.userPrompt as string | undefined,
					conversationId: data.conversationId as string | undefined,
				});
				return;
			}

			if (!isRendererPageSubmit(payload) || payload.type !== 'submit-rovo-remix') {
				return;
			}

			if (!isArmedRef.current) {
				return;
			}

			acceptPageRequest({
				type: payload.data.remixOption,
				subType: payload.data.remixSubType,
				userPrompt: payload.data.userPrompt,
				conversationId: payload.data.conversationId,
			});
		},
	);

	useSubscribe({ topic: Topics.AI_MATE }, (payload) => {
		if (payload.type === 'chat-close') {
			disarmIfIdle();
			return;
		}

		if (payload.type === 'chat-new' && payload.data.resetActiveMenu) {
			disarmIfIdle();
			return;
		}

		if (payload.type !== 'open-rovo-remix-sidebar') {
			return;
		}

		if (payload.data.remixConsumer === 'renderer' && payload.data.remixPreset === 'page') {
			isArmedRef.current = true;
			acceptedSubmitRef.current = false;
			originContentIdRef.current = currentContentId;
			return;
		}

		disarmIfIdle();
	});

	useEffect(() => {
		const originContentId = originContentIdRef.current;
		if (originContentId && currentContentId && originContentId !== currentContentId) {
			disarmIfIdle();
		}
	}, [currentContentId, disarmIfIdle, originContentIdRef]);

	return null;
};

const PAGE_REMIX_OBJECT_CONTENT_TYPES: Partial<Record<RemixOptionKind, string>> = {
	slides: 'slides',
	whiteboard: 'whiteboard',
	database: 'database',
};

const getPageRemixObjectCreationContext = (type: RemixOptionKind) => {
	if (type === 'infographic') {
		return { experience: 'remix_object' as const, blocks: ['infographic'] };
	}

	const contentType = PAGE_REMIX_OBJECT_CONTENT_TYPES[type];
	return contentType
		? { experience: 'remix_object' as const, contentTypes: [contentType] }
		: undefined;
};

const constructPageDynamicUiSource = (
	adfDocument: DocNode,
	contentType: DynamicUiSource['contentType'],
): DynamicUiSource => ({
	contentType,
	startLocalId: '',
	endLocalId: '',
	startIndex: 0,
	endIndex: -1,
	fragmentAdf: flattenDocContentForPageRemixFragment(adfDocument),
	fullAdf: adfDocument,
});

/** Remix types that open the AIFC staging modal directly, skipping the thinking bar. */
const OPEN_AIFC_MODAL_TYPES = new Set(['slides', 'whiteboard', 'database']);

/**
 * Maps page-level remix types to the forcedContentType value expected by the
 * backend.
 */
const PAGE_REMIX_FORCED_CONTENT_TYPE: Record<string, string> = {
	slides: 'slide',
	whiteboard: 'whiteboard',
	database: 'database',
};

type RemixAifcHandoffExperienceAttributes = {
	featureArea: string;
	entryPoint: InteractionSource;
	remixType: RemixOptionSelectionParameters['type'];
	remixSubtype?: string;
	aifcObjectType?: string;
	conversationChannelId?: string;
};

const getRemixAifcHandoffExperienceAttributes = ({
	type,
	subType,
	interactionSource,
	forcedContentType,
}: {
	type: RemixOptionSelectionParameters['type'];
	subType: string | undefined;
	interactionSource: InteractionSource;
	forcedContentType: string | undefined;
}): RemixAifcHandoffExperienceAttributes => ({
	featureArea:
		getRemixFeatureArea({
			remixType: type,
			existingFeatureArea: 'remix',
		}) ?? 'remix',
	entryPoint: interactionSource,
	remixType: type,
	...(subType && { remixSubtype: subType }),
	...(forcedContentType && { aifcObjectType: forcedContentType }),
});

export type PageRemixModalProps = {
	adfDocument?: DocNode | Promise<DocNode>;
	loadAdfDocument?: () => Promise<DocNode | null>;
	currentContentId?: string;
	interactionSource?: InteractionSource;
	onClose: () => void;
	onStopThinking: (options?: { reopenModal?: boolean }) => void;
	isEphemeralViewVisible: boolean;
	/** Render function for the trigger element that the popup anchors to. */
	trigger?: (triggerProps: TriggerProps) => React.ReactNode;
	/** Popup placement relative to the trigger. Defaults to 'left'. */
	popupPlacement?: 'left-end';
	/** Popup offset from the trigger in [along, away] px. */
	popupOffset?: [number, number];
	/** Whether the popup is open. */
	isPopupOpen?: boolean;
	/** When this value changes the popup will reposition itself. */
	popupRepositionKey?: number;
	/** Show the page-level title and subtitle above the first cascading-menu column. */
	showCascadingHeader?: boolean;
	presentation?: 'popup' | 'modal';
	/**
	 * Called when the sidebar should open with a specific conversation.
	 * Must be invoked from outside the ConfluenceConversationAssistant
	 * wrapper so the chat-open event reaches the sidebar's store.
	 */
	onOpenSidebarConversation?: (conversationId: string) => void;
};

export const PageRemixModal = ({
	adfDocument,
	loadAdfDocument,
	currentContentId,
	interactionSource = 'remixModalFromObjectLevelSidebar',
	onClose,
	onStopThinking,
	isEphemeralViewVisible,
	trigger,
	popupPlacement,
	popupOffset,
	isPopupOpen,
	popupRepositionKey,
	showCascadingHeader = false,
	presentation = 'popup',
	onOpenSidebarConversation,
}: PageRemixModalProps): React.JSX.Element => {
	const [pageContentId] = usePageContentId();
	const resolvedContentId = currentContentId ?? pageContentId;
	const [currentContentType] = useContentType();
	const remixDynamicUiContentType = resolveRemixDynamicUiContentType(currentContentType);
	const isRemixCancelled = useRef(false);
	const hasCommittedRemixCreateTerminal = useRef(false);
	const isDirectOpen = useRef(false);
	const activeAdfRequestId = useRef(0);
	const pendingCustomSidebarConversationIdRef = useRef<string | undefined>(undefined);
	const remixOriginContentIdRef = useRef<string | undefined>(undefined);
	const pendingResolvedAdfRef = useRef<DocNode | undefined>(undefined);
	const [isThinking, setIsThinking] = useState(false);
	const openAIFCStagingModalRef = useRef<() => void>(() => {});
	const remixAifcHandoffExperienceAttributesRef = useRef<
		RemixAifcHandoffExperienceAttributes | undefined
	>();

	const experienceTracker = useAIMateExperienceTracker();

	const abortRemixAifcHandoffExperience = useCallback(
		(reason: string) => {
			const attributes = remixAifcHandoffExperienceAttributesRef.current;
			if (!attributes) {
				return;
			}

			experienceTracker.abort({
				name: ExperienceName.REMIX_AIFC_HANDOFF,
				reason,
				attributes: { ...attributes, reason },
			});
			remixAifcHandoffExperienceAttributesRef.current = undefined;
		},
		[experienceTracker],
	);

	const cancelActiveRemixRequest = useCallback(() => {
		isRemixCancelled.current = true;
		activeAdfRequestId.current += 1;
		abortRemixAifcHandoffExperience('User cancelled Remix AIFC handoff');
	}, [abortRemixAifcHandoffExperience]);

	const hiddenOptions = useShouldHideDiagramRemixOption();

	const openModalAndCloseOverlay = useCallback(() => {
		hasCommittedRemixCreateTerminal.current = true;
		const pendingCustomSidebarConversationId = pendingCustomSidebarConversationIdRef.current;
		if (pendingCustomSidebarConversationId) {
			pendingCustomSidebarConversationIdRef.current = undefined;
			onOpenSidebarConversation?.(pendingCustomSidebarConversationId);
			onStopThinking();
			onClose();
			return;
		}

		openAIFCStagingModalRef.current();
		onStopThinking();
		onClose();
	}, [onClose, onOpenSidebarConversation, onStopThinking]);

	const {
		showProgressBar,
		isComplete,
		onCompleteAnimationFinished,
		signalActionReady,
		resetCompletion,
	} = useRemixThinkingBarProgress({ onOpenPreview: openModalAndCloseOverlay });

	const onActionReady = useCallback(() => {
		// For types in OPEN_AIFC_MODAL_TYPES, the modal is
		// already open — skip reopening it via onActionReady.
		if (isRemixCancelled.current || isDirectOpen.current) {
			return;
		}
		remixGeneratePreviewExperience.mark(REMIX_GENERATE_PREVIEW_MARKS.ACTION_COMPLETE_RECEIVED);
		experienceTracker.succeed({ name: ExperienceName.REMIX_START });
		void remixGeneratePreviewExperience.success();
		signalActionReady();
	}, [signalActionReady, experienceTracker]);

	const {
		sendAIFCPrompt,
		stopRemixing,
		isRemixError,
		isRemixAborted,
		openAIFCStagingModal,
		closeAIFCStagingModal,
	} = useRemixToAIFC({ onActionReady, stagingAreaTargetMode: 'generating-from-content' });
	openAIFCStagingModalRef.current = openAIFCStagingModal;

	const experienceTrackerRef = useRef(experienceTracker);
	experienceTrackerRef.current = experienceTracker;
	useEffect(() => {
		return () => {
			activeAdfRequestId.current += 1;
			experienceTrackerRef.current.abort({
				name: ExperienceName.REMIX_START,
				reason: 'User closed remix overlay',
				attributes: { reason: 'User closed remix overlay' },
			});
			abortRemixCreateExperienceIfNotCommitted(
				experienceTrackerRef.current,
				hasCommittedRemixCreateTerminal.current,
			);
			void remixGeneratePreviewExperience.abort({
				metadata: { reason: 'User closed remix overlay' },
			});
			if (!isDirectOpen.current) {
				abortRemixAifcHandoffExperience('User closed remix overlay');
			}
		};
	}, [abortRemixAifcHandoffExperience]);

	const handleClose = useCallback(() => {
		cancelActiveRemixRequest();
		onClose();
	}, [cancelActiveRemixRequest, onClose]);

	const onRemixFlowActiveChange = useCallback(
		(active: boolean) => {
			if (active) {
				isRemixCancelled.current = false;
				isDirectOpen.current = false;
				pendingCustomSidebarConversationIdRef.current = undefined;
				setIsThinking(true);
				// Clear stale completion state from any previous flow.
				resetCompletion();
			} else {
				setIsThinking(false);
				cancelActiveRemixRequest();
				isDirectOpen.current = false;
				pendingCustomSidebarConversationIdRef.current = undefined;
				experienceTracker.abort({
					name: ExperienceName.REMIX_START,
					reason: 'User cancelled remix thinking',
					attributes: { reason: 'User cancelled remix thinking' },
				});
				abortRemixCreateExperience(experienceTracker, 'User cancelled remix thinking');
				void remixGeneratePreviewExperience.abort({
					metadata: { reason: 'User cancelled remix thinking' },
				});
				stopRemixing();
				const restartAttributes = {
					entryPoint: PAGE_REMIX_ENTRY_POINT,
					remixSource: 'cancelledRemixThinking',
					originalRemixSource: PAGE_REMIX_SOURCE,
					product: 'confluence',
				};
				experienceTracker.start({
					name: ExperienceName.REMIX_START,
					id: ExperienceName.REMIX_START,
					attributes: restartAttributes,
				});
				startRemixCreateExperience({ experienceTracker, attributes: restartAttributes });
				onStopThinking({ reopenModal: true });
			}
		},
		[stopRemixing, onStopThinking, experienceTracker, resetCompletion, cancelActiveRemixRequest],
	);

	const prevIsRemixError = useRef(isRemixError);
	useEffect(() => {
		if (!prevIsRemixError.current && isRemixError && !isRemixCancelled.current) {
			experienceTracker.fail({
				name: ExperienceName.REMIX_START,
				error: new Error('Rovo agent error during remix'),
			});
			void remixGeneratePreviewExperience.failure({
				metadata: { reason: 'Rovo agent error during remix' },
			});
			onStopThinking({ reopenModal: true });
		}
		prevIsRemixError.current = isRemixError;
	}, [isRemixError, onStopThinking, experienceTracker]);

	useEffect(() => {
		if (!isRemixCancelled.current && isRemixError) {
			if (isEphemeralViewVisible) {
				closeAIFCStagingModal();
			}
			onStopThinking();
			onClose();
		}
	}, [isRemixError, isEphemeralViewVisible, closeAIFCStagingModal, onStopThinking, onClose]);

	// Dismiss the thinking bar and close the popup when the user stops the remix
	// from the Rovo side chat.
	useEffect(() => {
		if (isRemixAborted && !isRemixCancelled.current) {
			abortRemixCreateExperience(experienceTracker, 'User stopped from Rovo side chat');
			void remixGeneratePreviewExperience.abort({
				metadata: { reason: 'User stopped from Rovo side chat' },
			});
			if (isEphemeralViewVisible) {
				closeAIFCStagingModal();
			}
			onStopThinking();
			onClose();
		}
	}, [
		isRemixAborted,
		isEphemeralViewVisible,
		closeAIFCStagingModal,
		onStopThinking,
		onClose,
		experienceTracker,
	]);

	const handleOptionSelect = useCallback(
		async ({
			type,
			subType,
			userPrompt,
			conversationId,
		}: RemixOptionSelectionParameters & { conversationId?: string }) => {
			const isCustomOnlyPrompt = type === 'custom' && !subType && !!userPrompt?.trim();
			const shouldDeferCustomOnlyPrompt =
				isCustomOnlyPrompt && expValEquals('cc_maui_polish_changes_batch_5', 'isEnabled', true);
			const adfRequestId = activeAdfRequestId.current + 1;
			activeAdfRequestId.current = adfRequestId;
			const shouldReuseRovoConversation = Boolean(conversationId);

			experienceTracker.updateAttributes({
				name: ExperienceName.REMIX_START,
				attributes: {
					remixType: type,
					remixSubtype: subType,
					includesFreegenPrompt: !!userPrompt,
					...(conversationId && { conversationChannelId: conversationId }),
				},
			});
			const forcedContentType = PAGE_REMIX_FORCED_CONTENT_TYPE[type];
			const isRemixAifcHandoff = OPEN_AIFC_MODAL_TYPES.has(type);
			const remixAifcHandoffExperienceAttributes = isRemixAifcHandoff
				? getRemixAifcHandoffExperienceAttributes({
						type,
						subType,
						interactionSource,
						forcedContentType,
					})
				: undefined;

			if (remixAifcHandoffExperienceAttributes) {
				remixAifcHandoffExperienceAttributesRef.current = remixAifcHandoffExperienceAttributes;
				experienceTracker.start({
					name: ExperienceName.REMIX_AIFC_HANDOFF,
					id: ExperienceName.REMIX_AIFC_HANDOFF,
					attributes: remixAifcHandoffExperienceAttributes,
				});
			}

			void remixGeneratePreviewExperience.start();
			remixGeneratePreviewExperience.mark(REMIX_GENERATE_PREVIEW_MARKS.SUBMIT);
			remixGeneratePreviewExperience.addMetadata({
				product: 'confluence',
				interactionSource,
				remixType: type,
				remixSubtype: subType,
				entryPoint: PAGE_REMIX_ENTRY_POINT,
				contentType: remixDynamicUiContentType,
				includesFreegenPrompt: !!userPrompt,
				...(type === 'infographic'
					? {
							isConfluenceInfographicsTwoStageEnabled: fg('confluence-infographics-two-stage'),
						}
					: {}),
			});

			let resolvedAdfDocument: DocNode;
			try {
				if (pendingResolvedAdfRef.current) {
					resolvedAdfDocument = pendingResolvedAdfRef.current;
					pendingResolvedAdfRef.current = undefined;
				} else {
					const sourceAdf = adfDocument ?? EMPTY_PAGE_ADF;
					resolvedAdfDocument = sourceAdf instanceof Promise ? await sourceAdf : sourceAdf;
				}
			} catch (error) {
				if (activeAdfRequestId.current !== adfRequestId) {
					return;
				}

				if (
					UNSAFE_expValEqualsNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'variant2')
				) {
					publishPageRemixHandoffFailed();
				}

				experienceTracker.fail({
					name: ExperienceName.REMIX_START,
					error: error instanceof Error ? error : new Error('Failed to load content for Remix'),
				});
				void remixGeneratePreviewExperience.failure({
					metadata: { reason: 'Failed to load content for Remix' },
				});
				if (remixAifcHandoffExperienceAttributes) {
					experienceTracker.fail({
						name: ExperienceName.REMIX_AIFC_HANDOFF,
						error:
							error instanceof Error ? error : new Error('Failed to prepare Remix AIFC handoff'),
						attributes: remixAifcHandoffExperienceAttributes,
					});
					remixAifcHandoffExperienceAttributesRef.current = undefined;
				}
				onStopThinking();
				handleClose();
				return;
			}

			if (activeAdfRequestId.current !== adfRequestId) {
				return;
			}

			const dynamicUiSource = constructPageDynamicUiSource(
				resolvedAdfDocument,
				remixDynamicUiContentType,
			);
			const additionalCreationContextParams = getPageRemixObjectCreationContext(type);
			const promptText =
				type === 'slides' ? `Remix the selected content as ${getTypeDisplayName(type)}` : undefined;

			const promptArgs = {
				userPrompt,
				promptText,
				dynamicUiType: type,
				dynamicUiSubtype: subType,
				dynamicUiSource,
				interactionSource,
				additionalCreationContextParams,
				...(conversationId ? { conversationId } : {}),
			};

			if (!OPEN_AIFC_MODAL_TYPES.has(type)) {
				isDirectOpen.current = !shouldDeferCustomOnlyPrompt;
				const promptPromise = sendAIFCPrompt(promptArgs).then((conversationChannelId) => {
					if (conversationChannelId && !isRemixCancelled.current && !shouldReuseRovoConversation) {
						if (shouldDeferCustomOnlyPrompt) {
							pendingCustomSidebarConversationIdRef.current = conversationChannelId;
						} else {
							onOpenSidebarConversation?.(conversationChannelId);
						}
					}
					trackRemixCreateOptionSelected({
						experienceTracker,
						conversationId: conversationChannelId,
						remixType: type,
						remixSubtype: subType,
						includesFreegenPrompt: !!userPrompt,
					});
					return conversationChannelId;
				});

				if (shouldReuseRovoConversation) {
					try {
						await promptPromise;
					} catch (error) {
						if (
							UNSAFE_expValEqualsNoExposure(
								'cc-ce-maui-remix-menu-multivariant',
								'cohort',
								'variant2',
							)
						) {
							publishPageRemixHandoffFailed();
						}
						throw error;
					}
				} else {
					void promptPromise.catch(() => {
						if (
							UNSAFE_expValEqualsNoExposure(
								'cc-ce-maui-remix-menu-multivariant',
								'cohort',
								'variant2',
							)
						) {
							publishPageRemixHandoffFailed();
						}
					});
				}

				if (!shouldDeferCustomOnlyPrompt) {
					hasCommittedRemixCreateTerminal.current = true;
					experienceTracker.succeed({ name: ExperienceName.REMIX_START });
					void remixGeneratePreviewExperience.success();
					if (!shouldReuseRovoConversation) {
						onClose();
					}
				}
			} else {
				isDirectOpen.current = true;
				const promptPromise = sendAIFCPrompt({
					...promptArgs,
					forcedContentType,
				})
					.then((conversationChannelId) => {
						trackRemixCreateOptionSelected({
							experienceTracker,
							conversationId: conversationChannelId,
							remixType: type,
							remixSubtype: subType,
							includesFreegenPrompt: !!userPrompt,
						});
						if (!isRemixCancelled.current) {
							const attributes = {
								...remixAifcHandoffExperienceAttributes,
								...(conversationChannelId && { conversationChannelId }),
							};
							experienceTracker.succeed({
								name: ExperienceName.REMIX_AIFC_HANDOFF,
								attributes,
							});
							succeedRemixCreateExperienceAtHandoff(experienceTracker, {
								...remixAifcHandoffExperienceAttributes,
								...(conversationChannelId && { conversationId: conversationChannelId }),
							});
							remixAifcHandoffExperienceAttributesRef.current = undefined;
							experienceTracker.succeed({ name: ExperienceName.REMIX_START });
							void remixGeneratePreviewExperience.success();
							openAIFCStagingModal({ forceFullModal: true });
						}
					})
					.catch((error) => {
						if (!isRemixCancelled.current) {
							if (
								UNSAFE_expValEqualsNoExposure(
									'cc-ce-maui-remix-menu-multivariant',
									'cohort',
									'variant2',
								)
							) {
								publishPageRemixHandoffFailed();
							}
							const handoffError =
								error instanceof Error ? error : new Error('Failed to complete Remix AIFC handoff');

							experienceTracker.fail({
								name: ExperienceName.REMIX_AIFC_HANDOFF,
								error: handoffError,
								attributes: remixAifcHandoffExperienceAttributes,
							});
							experienceTracker.fail({
								name: ExperienceName.REMIX_START,
								error: handoffError,
							});
							void remixGeneratePreviewExperience.failure({
								metadata: { reason: 'Failed to complete Remix AIFC handoff' },
							});
							remixAifcHandoffExperienceAttributesRef.current = undefined;
						}
						if (shouldReuseRovoConversation) {
							throw error;
						}
					});
				if (shouldReuseRovoConversation) {
					await promptPromise;
				}
				hasCommittedRemixCreateTerminal.current = true;
				onClose();
			}
		},
		[
			sendAIFCPrompt,
			adfDocument,
			interactionSource,
			openAIFCStagingModal,
			onClose,
			onStopThinking,
			experienceTracker,
			onOpenSidebarConversation,
			handleClose,
			remixDynamicUiContentType,
		],
	);

	const disarmIdlePageRemix = useCallback(() => {
		pendingResolvedAdfRef.current = undefined;
	}, []);

	const handleVariantTwoPageRemixRequest = useCallback(
		async (params: PageRemixRequestParameters) => {
			let resolvedAdf: DocNode | null = null;
			try {
				if (loadAdfDocument) {
					resolvedAdf = await loadAdfDocument();
				} else if (adfDocument instanceof Promise) {
					resolvedAdf = await adfDocument;
				} else {
					resolvedAdf = adfDocument ?? null;
				}
			} catch {
				publishPageRemixHandoffFailed();
				throw new Error('Failed to load page ADF for Remix');
			}
			if (!resolvedAdf || isEmptyPageAdf(resolvedAdf)) {
				publishPageRemixHandoffFailed();
				throw new Error('Page content is empty');
			}

			pendingResolvedAdfRef.current = resolvedAdf;
			onRemixFlowActiveChange(true);
			try {
				await handleOptionSelect(params);
			} catch {
				onStopThinking({ reopenModal: true });
				throw new Error('Failed to start page remix from Rovo submit');
			}
		},
		[adfDocument, handleOptionSelect, loadAdfDocument, onRemixFlowActiveChange, onStopThinking],
	);

	const shouldShowExistingRemixTypeMenu =
		isThinking ||
		!UNSAFE_expValEqualsNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'variant2');

	return (
		<>
			{UNSAFE_expValEqualsNoExposure(
				'cc-ce-maui-remix-menu-multivariant',
				'cohort',
				'variant2',
			) && (
				<PageRemixSubmitListener
					currentContentId={resolvedContentId}
					hasGenerationStarted={isThinking}
					onAcceptSubmit={handleVariantTwoPageRemixRequest}
					onDisarm={disarmIdlePageRemix}
					originContentIdRef={remixOriginContentIdRef}
				/>
			)}
			{shouldShowExistingRemixTypeMenu && (
				<RemixContainer
					onClose={handleClose}
					onOptionSelect={handleOptionSelect}
					onRemixFlowActiveChange={onRemixFlowActiveChange}
					isEphemeralViewVisible={isEphemeralViewVisible}
					skipThinkingTypes={
						new Set([
							...OPEN_AIFC_MODAL_TYPES,
							'infographic',
							...(expValEquals('cc_maui_polish_changes_batch_5', 'isEnabled', true)
								? []
								: ['custom']),
						])
					}
					options={PAGE_REMIX_OPTIONS}
					hiddenOptions={hiddenOptions}
					subtitle={remixOverlayMessages.pageRemixSubtitle}
					cascadingMenuHeader={
						showCascadingHeader
							? {
									title: remixOverlayMessages.heading,
									subtitle: remixOverlayMessages.pageRemixSubtitle,
								}
							: undefined
					}
					presentation={presentation}
					trigger={trigger}
					popupPlacement={popupPlacement}
					popupOffset={popupOffset}
					isPopupOpen={isPopupOpen}
					popupRepositionKey={popupRepositionKey}
					showProgressBar={showProgressBar}
					isComplete={isComplete}
					onCompleteAnimationFinished={onCompleteAnimationFinished}
					isThinking={
						isThinking &&
						UNSAFE_expValEqualsNoExposure(
							'cc-ce-maui-remix-menu-multivariant',
							'cohort',
							'variant2',
						)
							? true
							: undefined
					}
				/>
			)}
		</>
	);
};

export type PageRemixSubmitOwnerProps = {
	loadAdfDocument?: () => Promise<DocNode | null>;
	onOpenSidebarConversation?: (conversationId: string) => void;
	onClose?: () => void;
	onStopThinking?: (options?: { reopenModal?: boolean }) => void;
};

export const PageRemixSubmitOwner = ({
	loadAdfDocument,
	onOpenSidebarConversation,
	onClose = () => {},
	onStopThinking = () => {},
}: PageRemixSubmitOwnerProps): React.JSX.Element => {
	const { showSmartCreationModal } = useSmartCreationModalState();
	const [pageContentId] = usePageContentId();

	return (
		<ConfluenceConversationAssistant>
			<PageRemixModal
				loadAdfDocument={loadAdfDocument}
				currentContentId={pageContentId}
				onClose={onClose}
				onStopThinking={onStopThinking}
				isEphemeralViewVisible={showSmartCreationModal}
				onOpenSidebarConversation={onOpenSidebarConversation}
			/>
		</ConfluenceConversationAssistant>
	);
};

export const EditorPageRemixModal = (
	props: Extract<RemixContentComponentProps, { remixType: 'object' }>,
): React.JSX.Element => {
	const { showSmartCreationModal } = useSmartCreationModalState();
	const interactionSource = props.remixModalMetadata?.interactionSource;

	return (
		<PageRemixModal
			adfDocument={props.adfDocument ?? { type: 'doc', version: 1, content: [] }}
			onClose={props.handleModalClose}
			onStopThinking={props.onStopThinking}
			isEphemeralViewVisible={showSmartCreationModal}
			interactionSource={interactionSource}
			presentation="modal"
		/>
	);
};
